// Имя человека по опознавателю.
//
// С 02.09.2026 имя приходит С СЕРВЕРА: рядом с каждым `owner_user_id`, `added_by`, `changed_by`
// в ответе найма стоит `*_name`. До этого его не было нигде, справочник людей
// (`/api/v1/users`) закрыт правом `core.user.view`, которого нет ни у рекрутёра, ни у кадров, —
// и экран показывал шестнадцатеричную строку там, где должно стоять имя.
//
// Поэтому первым делом берётся имя из ответа, и только если его нет — справочник (он есть у
// администратора и руководства) и собственная карточка. Порядок именно такой: ответ найма
// знает имя всегда, а справочник — только для тех, кому он открыт.
//
// Правило файла не изменилось: **имя либо настоящее, либо его нет**. Показывать «пользователь
// 9f3c…» нельзя — служебное обозначение человеку не говорит ничего (`.claude/rules/ui-views.md`,
// «Служебные обозначения человеку не показываются»). Пусто — честный ответ, и рядом с ним на
// экране стоит объяснение, почему пусто.

import { useQuery } from '@tanstack/react-query';

import { listUsers } from '@/shared/api/endpoints/users';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';

const USERS_PERMISSION = 'core.user.view';

export interface UserNames {
  /**
   * Имя по опознавателю. `null` — имени нет и выдумывать его нечем.
   *
   * `сСервера` — имя из того же ответа найма, если оно там есть. Оно всегда точнее: справочник
   * людей открыт не всем, а ответ найма несёт имя каждому, кто имеет право видеть саму запись.
   */
  имя: (userId: string | null | undefined, сСервера?: string | null) => string | null;
  /** Отдаёт ли сервер справочник этой роли. По нему экран решает, объяснять ли пустоту. */
  справочникДоступен: boolean;
}

export function useUserNames(): UserNames {
  const permissions = useAuthStore((state) => state.permissions);
  const me = useAuthStore((state) => state.user);
  const доступен = can(permissions, USERS_PERMISSION);

  const справочник = useQuery({
    queryKey: ['users', 'list', 'active'],
    queryFn: ({ signal }) => listUsers({ active_only: true }, signal),
    enabled: доступен,
    staleTime: 5 * 60 * 1000,
  });

  return {
    справочникДоступен: доступен,
    имя: (userId, сСервера) => {
      if (сСервера !== null && сСервера !== undefined && сСервера !== '') {
        return сСервера;
      }
      if (userId === null || userId === undefined) {
        return null;
      }
      // Себя человек узнаёт всегда, даже без справочника: его собственная карточка приезжает
      // вместе с сессией. Это тот случай, когда «мои кандидаты» и есть половина вопроса.
      if (me !== null && userId === me.id) {
        return me.display_name;
      }
      const найден = справочник.data?.items.find((user) => user.id === userId);
      return найден?.display_name ?? null;
    },
  };
}
